import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { QuestionGroupTemplateContract } from '@/contracts/question-groups'
import { createQuestionGroupTemplate } from '@/lib/data-client/question-groups'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

type QuestionGroupFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (template: QuestionGroupTemplateContract) => void
}

export function QuestionGroupFormDialog({
  open,
  onOpenChange,
  onCreated,
}: QuestionGroupFormDialogProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('客戶建檔')
  const [tags, setTags] = useState('B2B, 客戶')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setTitle('')
    setDescription('')
    setCategory('客戶建檔')
    setTags('B2B, 客戶')
  }, [open])

  const normalizedTags = useMemo(
    () =>
      tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    [tags],
  )

  async function createGroup() {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) return
    setSaving(true)
    try {
      const template = await createQuestionGroupTemplate({
        title: trimmedTitle,
        description: description.trim() || undefined,
        category: category.trim() || undefined,
        tags: normalizedTags,
      })
      onCreated(template)
      onOpenChange(false)
      toast.success('已建立題組')
    } catch (error) {
      console.error('[MiaoForm] create question group template failed', error)
      toast.error('目前無法建立題組，請稍後再試。')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>新增題組</DialogTitle>
          <DialogDescription>建立可重用的題組模板，供問卷編輯時引用。</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question-group-title">題組標題</Label>
            <Input
              id="question-group-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="例如：公司資料"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="question-group-description">描述</Label>
            <Textarea
              id="question-group-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="說明此題組適合收集哪些資料"
              className="min-h-24"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="question-group-category">分類</Label>
              <Input
                id="question-group-category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                placeholder="客戶建檔"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="question-group-tags">標籤</Label>
              <Input
                id="question-group-tags"
                value={tags}
                onChange={(event) => setTags(event.target.value)}
                placeholder="以逗號分隔"
              />
            </div>
          </div>
          {normalizedTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {normalizedTags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="button" onClick={createGroup} disabled={!title.trim() || saving}>
              {saving ? '建立中...' : '建立題組'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
